<template>
  <div>
    <e-filters
      :searching="fetching"
      @search="filterData"
      @reset="resetFiltersLocal"
    >
      <div>
        <b-row>
          <b-col md="3">
            <e-store-combo
              id="filters-store"
              v-model="filters.storeId"
              :required="false"
              :select-single-option="false"
              :placeholder="$t('Todas')"
            />
          </b-col>

          <b-col md="3">
            <FormulateInput
              id="filters-card-number"
              v-model="filters.cardNumber"
              type="text"
              :label="$t('Número do cartão')"
              :placeholder="$t('Digite o número do cartão')"
            />
          </b-col>

          <b-col md="3">
            <e-user-search
              id="filters-user"
              v-model="filters.userId"
              :required="false"
              :label="$t('Usuário')"
            />
          </b-col>

          <b-col md="3">
            <FormulateInput
              id="filters-status"
              v-model="filters.active"
              type="vue-select"
              :label="$t('Status')"
              :placeholder="$t('Todos')"
              :options="activeInactiveTypes()"
            />
          </b-col>
        </b-row>
      </div>
    </e-filters>

    <b-card-actions
      class="mb-2"
      action-refresh
      :show-loading="fetching"
      @refresh="filterData"
    >
      <div slot="title">
        <e-page-size-selector
          :per-page="paging.pageSize"
          @change="pageSizeChange"
        />
      </div>
      <b-table
        show-empty
        responsive
        striped
        class="bordered"
        :empty-text="getEmptyTableMessage($t('cartões'), 'male', queryExecuted)"
        :fields="fields"
        :items="adminCards"
        no-local-sorting
        :sort-by.sync="sorting.sortBy"
        :sort-desc.sync="sorting.sortDesc"
        @sort-changed="getData"
      >
        <template #cell(active)="row">
          <e-status-badge :status="row.item.active" />
        </template>

        <template #cell(stores)="row">
          <span v-if="row.item.stores && row.item.stores.length">
            {{ formatStores(row.item.stores) }}
          </span>
          <span v-else>-</span>
        </template>

        <template #cell(payBoxes)="row">
          <span v-if="row.item.payBoxes && row.item.payBoxes.length">
            {{ formatPayBoxes(row.item.payBoxes) }}
          </span>
          <span v-else>-</span>
        </template>

        <template #cell(action)="row">
          <e-grid-actions
            :show-update="$can('Update', 'AdminCard')"
            :show-delete="$can('Delete', 'AdminCard')"
            @update="onShowSidebar(row.item)"
            @delete="onDelete(row.item)"
          />
        </template>
      </b-table>

      <b-row>
        <b-col cols="6">
          <e-pagination-summary
            :current-page="paging.currentPage"
            :per-page="paging.pageSize"
            :total="paging.rowCount"
            :total-on-page="paging.rowsInCurrentPage"
          />
        </b-col>
        <b-col cols="6">
          <b-pagination
            v-model="paging.currentPage"
            align="right"
            :total-rows="paging.rowCount"
            :per-page="paging.pageSize"
            aria-controls="table-manage-admin-cards"
            @change="pageChange"
          />
        </b-col>
      </b-row>
    </b-card-actions>

    <manage-admin-cards-sidebar
      ref="manageAdminCardsSidebar"
      @after-save="getData"
    />

    <fab
      v-if="$can('Create', 'AdminCard')"
      :main-tooltip="$t('Adicionar novo Cartão')"
      @click="onShowSidebar"
    />
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import { BRow, BCol, BPagination, BTable } from 'bootstrap-vue'
import BCardActions from '@core/components/b-card-actions/BCardActions.vue'
import { EPageSizeSelector, EPaginationSummary, EGridActions } from '@/views/components'
import fab from '@/views/components/FAB.vue'
import { statusTypes, translate } from '@/mixins'
import EStatusBadge from '@/views/components/EStatusBadge.vue'
import EFilters from '@/views/components/EFilters.vue'
import EUserSearch from '@/views/components/inputs/EUserSearch.vue'
import EStoreCombo from '@/views/components/inputs/EStoreCombo.vue'
import ManageAdminCardsSidebar from './ManageAdminCardsSidebar.vue'

export default {
  components: {
    BRow,
    BCol,
    BCardActions,
    BPagination,
    BTable,
    EPaginationSummary,
    EPageSizeSelector,
    EGridActions,
    fab,
    EStatusBadge,
    EFilters,
    EUserSearch,
    EStoreCombo,
    ManageAdminCardsSidebar,
  },

  mixins: [statusTypes, translate],

  data() {
    return {
      queryExecuted: false,
      fetching: false,
    }
  },

  computed: {
    ...mapState('pages/pdv/manageAdminCards', {
      adminCards: 'adminCards',
      paging: 'paging',
      sorting: 'sorting',
      filters: 'filters',
    }),

    fields() {
      return [
        {
          label: this.$t('Ações'),
          key: 'action',
          thClass: 'text-center',
          tdClass: 'text-center',
          thStyle: { width: '100px' },
        },
        {
          label: this.$t('Código'),
          key: 'id',
          thClass: 'text-center',
          tdClass: 'text-center',
          thStyle: { width: '100px' },
          sortable: true,
        },
        {
          label: this.$t('Número do cartão'),
          key: 'cardNumber',
          tdClass: 'text-left',
          sortable: true,
        },
        {
          label: this.$t('Usuário'),
          key: 'userName',
          tdClass: 'text-left',
          formatter: (val, index, item) => item.userName || item.userId,
        },
        {
          label: this.$t('Lojas com acesso total'),
          key: 'stores',
          tdClass: 'text-left',
        },
        {
          label: this.$t('Caixas (PDV) específicos'),
          key: 'payBoxes',
          tdClass: 'text-left',
        },
        {
          label: this.$t('Status'),
          key: 'active',
          tdClass: 'text-center',
          thStyle: { width: '100px' },
          sortable: true,
        },
      ]
    },
  },

  methods: {
    ...mapActions('pages/pdv/manageAdminCards', [
      'fetchAdminCards',
      'setCurrentPage',
      'setPageSize',
      'resetFilters',
    ]),

    async getData() {
      this.$nextTick(async () => {
        try {
          this.fetching = true
          await this.fetchAdminCards()
          this.queryExecuted = true
        } catch (error) {
          this.showGenericError({ error })
        } finally {
          this.fetching = false
        }
      })
    },

    formatPayBoxes(payBoxes) {
      return payBoxes
        .map(pb => {
          const description = [pb.payBoxNumber, pb.payBoxDescription].filter(Boolean).join(' - ')
          return pb.storeName ? `${pb.storeName} • ${description}` : description
        })
        .filter(text => text.length)
        .join(', ')
    },

    formatStores(stores) {
      return stores
        .map(s => s.storeName || `#${s.storeId}`)
        .filter(text => text.length)
        .join(', ')
    },

    resetFiltersLocal() {
      this.resetFilters()
      this.getData()
    },

    filterData() {
      this.setCurrentPage(1)
      this.getData()
    },

    pageSizeChange(pageSize) {
      this.setPageSize(pageSize)
      this.getData()
    },

    pageChange(currentPage) {
      this.setCurrentPage(currentPage)
      this.getData()
    },

    onShowSidebar(item) {
      this.$refs.manageAdminCardsSidebar.show(item)
    },

    async onDelete(item) {
      try {
        const confirmed = await this.confirm({
          text: this.$t('Tem certeza que deseja excluir este cartão?'),
        })

        if (!confirmed) return

        await this.$http({
          url: `/api/admin-cards/${item.id}`,
          method: 'DELETE',
        })

        this.showSuccess({ message: this.$t('Excluído com sucesso') })
        this.getData()
      } catch (error) {
        this.showError({ error })
      }
    },
  },
}
</script>

<style lang="scss" scoped></style>
